import type {AxiosResponse, InternalAxiosRequestConfig} from "axios";
import axios from "axios";
import {isCompanyChurnedError} from "./utils/companyChurned.util.js";
import {Storage} from "./utils/storage.util.js";

import {authenticationApiService} from "./authentication/authenticationApi.service.js";
import {finishAxiosDiagnostic, startAxiosDiagnostic} from "./diagnostics/networkDiagnostics.js";
import {queryService} from "./services/query.service.js";

const TIMEOUT = 1 * 60 * 1000;

export function initAxios() {
  axios.defaults.timeout = TIMEOUT;
  axios.defaults.transitional = {...axios.defaults.transitional, clarifyTimeoutError: true};
}

export const setupAxiosInterceptors = () => {
  let isLoggingOut = false;

  function forceLogout() {
    if (isLoggingOut) {
      return;
    }
    isLoggingOut = true;
    authenticationApiService.logout();
    queryService.getQueryClient().clear();
    window.location.replace(new URL("login", document.baseURI).toString());
  }

  function redirectToCompteEnVeille() {
    /**
     * Le tunnel public de réservation (#429) reste ouvert à un visiteur anonyme
     * même quand un token encore valide d'un pro en veille traîne dans le
     * navigateur : le rediriger casserait sa réservation en cours, sans rapport
     * avec lui.
     */
    if (window.location.pathname.includes("/pro/")) {
      return;
    }
    const target = new URL("compte-en-veille", document.baseURI);
    if (window.location.pathname !== target.pathname) {
      window.location.replace(target.toString());
    }
  }

  const onRequestSuccess = (config: InternalAxiosRequestConfig) => {
    const token =
      Storage.local.get("jhi-authenticationToken") ||
      Storage.session.get("jhi-authenticationToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    startAxiosDiagnostic(config);
    return config;
  };
  const onResponseSuccess = (response: AxiosResponse) => {
    finishAxiosDiagnostic(response.config, undefined, response.status);
    return response;
  };
  const onResponseError = (err: any) => {
    finishAxiosDiagnostic(err.config, err);
    const status = err.status || (err.response ? err.response.status : 0);
    if (isCompanyChurnedError(err, status)) {
      redirectToCompteEnVeille();
    } else if (status === 401) {
      forceLogout();
    }
    return Promise.reject(err);
  };
  axios.interceptors.request.use(onRequestSuccess);
  axios.interceptors.response.use(onResponseSuccess, onResponseError);
};
